import type { SymmetryAxis } from "@pdfking/filling"
import type { Degree } from "@pdfking/math"
import { FlipMargins, Subtract } from "@pdfking/math"
import { HALF_TURN, ZERO_DEGREE } from "@pdfking/math/constants"

import { ALONG_AXIS } from "../data/AlongAxis"
import type { Layout } from "../types/layout"
import { QuarterTurn, TurnMargins } from "./TurnMargins"

// The turn seen from the other side: turned over on x a thing keeps its head
// up and a turned one faces the other way, turned over on y every head points
// down.
const facing = (rotation: Degree | undefined, axis: SymmetryAxis): Degree =>
  QuarterTurn(Subtract(axis === "x" ? ZERO_DEGREE : HALF_TURN, rotation ?? ZERO_DEGREE))

/**
 * The layout seen from the other side of the sheet, turned over about `axis`:
 * every stack along the axis runs the other way, and every page changes side.
 *
 * A page's facing is mirrored with it, and its padding, its own, follows the
 * box it now takes. A container's turn is mirrored the same way. Flipping
 * twice gives the layout back.
 */
export const FlipLayout = <D>(layout: Layout<D>, axis: SymmetryAxis): Layout<D> => {
  if (layout.enum === "page") {
    const rotation = layout.rotation ?? ZERO_DEGREE
    const flipped = facing(rotation, axis)
    return {
      ...layout,
      is_front: !(layout.is_front ?? true),
      padding:
        layout.padding &&
        TurnMargins(
          FlipMargins(TurnMargins(layout.padding, rotation), axis),
          Subtract(ZERO_DEGREE, flipped)
        ),
      rotation: flipped,
    }
  }
  const layouts = (layout.layouts ?? []).map(child => FlipLayout(child, axis))
  return {
    ...layout,
    is_front: !(layout.is_front ?? true),
    layouts: (layout.direction ?? "row") === ALONG_AXIS[axis] ? layouts.reverse() : layouts,
    padding: layout.padding && FlipMargins(layout.padding, axis),
    rotation: facing(layout.rotation, axis),
  }
}
